import React from "react";
import { useState } from "owa-react";
import { Button, Tooltip } from "@fluentui/react-components";
import { ChevronLeft12Filled, ChevronRight12Filled } from "@fluentui/react-icons";
import { observer } from "owa-mobx-react";
import classnames from "owa-classnames";
import { useResizeObserver } from "owa-react-hooks/lib/useResizeObserver";
import { setShowFolderPane, setShowFolderPaneAsTransientOverlay, shouldShowFavoritesSideRail } from "owa-mail-layout";
import { getFavoriteIdFromFolderId, getStore as getSharedFavoritesStore } from "owa-favorites";
import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { isMonarchMultipleAccountsEnabled } from "owa-account-source-list";
import { getAccountKeyForMailboxInfo } from "owa-client-types";
import { getIsBitSet, type ListViewBitFlagsMasks } from "owa-bit-flags/lib/utils/listViewBitFlagsUtil";
import { isCapabilityEnabled } from "owa-capabilities";
import { bleedThroughCapability } from "owa-capabilities-definitions/lib/bleedThroughCapability";
import loc, { isCurrentCultureRightToLeft } from "owa-localize";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";
import folderStore from "owa-folders";
import publicFolderFavoriteStore from "owa-public-folder-favorite/lib/store/publicFolderFavoriteStore";
import type { FavoriteFolderData, FavoritePublicFolderData } from "owa-favorites-types";
import { expandNavigationPaneButton as Strings_expandNavigationPaneButton } from "./FavoritesSideRail.locstring.json";
import { getFavoritesSideRailCandidateItems, getVisibleFavoritesSideRailItems, } from "../util/getFavoritesSideRailItems";
import useRetainedFolderForFavoritesSideRail from "../hooks/useRetainedFolderForFavoritesSideRail";
import { type FolderForestNodeType } from "owa-favorites-types";
import type { FavoritesSideRailItem } from "../types/FavoritesSideRailItem";
import FavoritesSideRailNode from "./FavoritesSideRailNode";
import { sideRailContainer, sideRailContainerRtl, sideRailContainerBleedThrough, sideRailContainerBleedThroughRtl, sideRail, sideRailHeader, sideRailToggleButton, sideRailToggleIcon, sideRailItemList, } from "./FavoritesSideRail.scss";

const MAX_FAVORITES = 20;
const ITEM_GAP_PX = 2;
const LIST_HEIGHT_RATIO = 0.9; // Leave 10% height buffer
const RETAINED_FOLDER_ITEM_PREFIX = "retained-folder:";

const FavoritesSideRail: React.FunctionComponent = observer(function FavoritesSideRail() {
    const railRef = React.useRef<HTMLDivElement>(null);
    const headerRef = React.useRef<HTMLDivElement>(null);
    const headerHeightRef = React.useRef(0);
    
    const favoritesStore = getSharedFavoritesStore();
    const isSideRailVisible = shouldShowFavoritesSideRail();
    const retainedFolder = useRetainedFolderForFavoritesSideRail(isSideRailVisible);
    
    const allOrdered = isMonarchMultipleAccountsEnabled()
        ? favoritesStore.orderedCombinedOutlookFavoritesIds
        : (favoritesStore.favoriteTreeData.get(getAccountKeyForMailboxInfo(getGlobalSettingsAccountMailboxInfo()))?.orderedOutlookFavoritesIds ?? []);
    
    const validFavoriteIds = allOrdered.filter(id => {
        const data = favoritesStore.outlookFavorites.get(id);
        if (!data) {
            return false;
        }
        const type = data.type;
        if (type === "folder" ||
            type === "localFolderArchive" ||
            type === "localFolderShared" ||
            type === "localFolderPst") {
            return !!folderStore().folderTable.get((data as FavoriteFolderData).folderId);
        }
        if (type === "publicFolder") {
            return !!publicFolderFavoriteStore().folderTable.get((data as FavoritePublicFolderData).publicFolderId);
        }
        return true;
    });
    const favoriteItems: FavoritesSideRailItem[] = validFavoriteIds.map(favoriteId => ({
        kind: "favorite",
        key: favoriteId,
        favoriteId,
    }));
    const retainedFavoriteId = retainedFolder
        ? getFavoriteIdFromFolderId(retainedFolder.folderId)
        : undefined;
    const retainedFavoriteItem = favoriteItems.find(item => item.key === retainedFavoriteId);
    const retainedNonFavoriteFolder = !retainedFavoriteItem &&
        retainedFolder?.nodeType === 0 &&
        folderStore().folderTable.has(retainedFolder.folderId)
        ? retainedFolder
        : undefined;
    const retainedFolderItem: FavoritesSideRailItem | undefined = retainedNonFavoriteFolder
        ? {
            kind: "folder",
            key: `${RETAINED_FOLDER_ITEM_PREFIX}${retainedNonFavoriteFolder.treeType}:${retainedNonFavoriteFolder.folderId}`,
            folderId: retainedNonFavoriteFolder.folderId,
            treeType: retainedNonFavoriteFolder.treeType,
        }
        : undefined;
    const retainedItem = retainedFavoriteItem ?? retainedFolderItem;
    const retainedItemKey = retainedItem?.key;
    
    const candidateItems = getFavoritesSideRailCandidateItems(favoriteItems, retainedItem, MAX_FAVORITES);
    
    const [capacity, setCapacity] = useState(candidateItems.length);
    const [shouldPlaceRetainedItemAtBottom, setShouldPlaceRetainedItemAtBottom] = useState(false);
    const [measuredCandidateCount, setMeasuredCandidateCount] = useState(candidateItems.length);
    
    const itemHeightsRef = React.useRef<Map<string, number>>(new Map());
    const candidateItemsRef = React.useRef(candidateItems);
    candidateItemsRef.current = candidateItems;
    const retainedItemKeyRef = React.useRef(retainedItemKey);
    retainedItemKeyRef.current = retainedItemKey;
    const railHeightRef = React.useRef(0);
    
    const calculateItemsToShow = React.useCallback(() => {
        const availableListHeight = Math.max(0, railHeightRef.current - headerHeightRef.current);
        const maxHeight = availableListHeight * LIST_HEIGHT_RATIO;
        if (maxHeight <= 0) {
            return;
        }
        
        const nextVisibleItems = getVisibleFavoritesSideRailItems(candidateItemsRef.current, retainedItemKeyRef.current, itemHeightsRef.current, maxHeight, ITEM_GAP_PX);
        if (nextVisibleItems) {
            const naturalVisibleItems = candidateItemsRef.current.slice(0, nextVisibleItems.length);
            setCapacity(nextVisibleItems.length);
            setShouldPlaceRetainedItemAtBottom(nextVisibleItems.some((item, index) => item.key !== naturalVisibleItems[index]?.key));
            setMeasuredCandidateCount(candidateItemsRef.current.length);
        }
    }, []);
    
    const onItemHeightChange = React.useCallback((itemId: string, height: number) => {
        itemHeightsRef.current.set(itemId, height);
        calculateItemsToShow();
    }, [calculateItemsToShow]);
    
    // Resets the visible set when the rail grows so previously-trimmed items get a chance to
    // mount and remeasure.
    const previousRailHeightRef = React.useRef(0);
    const onRailResize = React.useCallback((bounds: DOMRectReadOnly) => {
        const previousHeight = previousRailHeightRef.current;
        previousRailHeightRef.current = bounds.height;
        railHeightRef.current = bounds.height;
        if (bounds.height > previousHeight) {
            setCapacity(candidateItemsRef.current.length);
            setShouldPlaceRetainedItemAtBottom(false);
        }
        calculateItemsToShow();
    }, [calculateItemsToShow]);
    useResizeObserver("FavoritesSideRail", railRef, onRailResize);
    
    const onHeaderResize = React.useCallback((bounds: DOMRectReadOnly) => {
        headerHeightRef.current = bounds.height;
        calculateItemsToShow();
    }, [calculateItemsToShow]);
    useResizeObserver("FavoritesSideRailHeader", headerRef, onHeaderResize);
    
    const candidateCountIncreased = candidateItems.length > measuredCandidateCount;
    
    const showWin32OverlayUI = isWin32StylingEnabled();
    const shouldHideFavoritesList = getIsBitSet(16384, getGlobalSettingsAccountMailboxInfo());
    
    const onExpandPane = React.useCallback(() => {
        if (showWin32OverlayUI) {
            setShowFolderPaneAsTransientOverlay(true);
            return;
        }
        
        setShowFolderPane(true);
    }, [showWin32OverlayUI]);
    
    if (!isSideRailVisible) {
        return null;
    }
    
    // Preserve legacy behavior when overlay styling is disabled.
    if (!showWin32OverlayUI && candidateItems.length === 0) {
        return null;
    }
    
    const isBleedThroughEnabled = isCapabilityEnabled(bleedThroughCapability);
    const isRTL = isCurrentCultureRightToLeft();
    const expandPaneLabel = loc(Strings_expandNavigationPaneButton);
    /* oxlint-disable-next-line react-perf/jsx-no-jsx-as-prop -- (https://aka.ms/OWALintWiki)
     * Fix this by hoisting the static icon elements or memoizing the selected icon so its identity
     * remains stable across renders.
     * > JSX attribute values should not contain other JSX. */
    const expandPaneIcon = isRTL ? (<ChevronLeft12Filled className={sideRailToggleIcon}/>) : (<ChevronRight12Filled className={sideRailToggleIcon}/>);
    const renderCapacity = candidateCountIncreased ? candidateItems.length : capacity;
    const shouldRenderRetainedItemAtBottom = !candidateCountIncreased && shouldPlaceRetainedItemAtBottom && retainedItem;
    const visibleItems = shouldRenderRetainedItemAtBottom
        ? [...candidateItems.slice(0, Math.max(0, renderCapacity - 1)), retainedItem]
        : candidateItems.slice(0, renderCapacity);
    
    return (<div className={classnames(sideRailContainer, isRTL && sideRailContainerRtl, isBleedThroughEnabled && sideRailContainerBleedThrough, isBleedThroughEnabled && isRTL && sideRailContainerBleedThroughRtl)}>
            <div ref={railRef} className={sideRail}>
                {showWin32OverlayUI && (<div ref={headerRef} className={sideRailHeader}>
                        <Tooltip content={expandPaneLabel} relationship="label" positioning="after">
                            <Button appearance="subtle" icon={expandPaneIcon} aria-label={expandPaneLabel} className={sideRailToggleButton} onClick={onExpandPane}/>
                        </Tooltip>
                    </div>)}
                {!shouldHideFavoritesList && visibleItems.length > 0 && (<div className={sideRailItemList} role="list" aria-label="Favorites">
                        {visibleItems.map(item => item.kind === "favorite" ? (<FavoritesSideRailNode key={item.key} kind="favorite" itemKey={item.key} favoriteId={item.favoriteId} hideIcon={!isGlobalFeatureEnabled("fp-vertical-favorites-icons")} onHeightChange={onItemHeightChange}/>) : (<FavoritesSideRailNode key={item.key} kind="folder" itemKey={item.key} folderId={item.folderId} treeType={item.treeType} hideIcon={!isGlobalFeatureEnabled("fp-vertical-favorites-icons")} onHeightChange={onItemHeightChange}/>))}
                    </div>)}
            </div>
        </div>);
}, "FavoritesSideRail");

export default FavoritesSideRail;
